import pluralize from 'pluralize';
import { useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Board, type Clip } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { Modal, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';

import { useCreatePrivateAssetBoardMembershipUpdateTask } from '~/components/AssetBoardMembershipUpdateTasks/useCreatePrivateAssetBoardMembershipUpdateTask';
import { type BoardSearchOption } from '~/components/BoardSearch/PrivateBoardSearch';
import { type BoardSearchLibrary } from '~/components/BoardSearch/shared/hooks';
import { ToastLink } from '~/components/UI/ToastLink';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { useMoveItems } from '~/hooks/useMoveItems';
import { useAddAssetsToBoards } from '~/swr-hooks/boards/useAddAssetsToBoards';

export type MoveItemsConfirmationModalProps = {
  boards: Board[];
  clips?: Pick<Clip, 'assetId' | 'id' | 'openCommentCount'>[];
  selectedBoard: BoardSearchOption | null;
  selectedLibrary?: BoardSearchLibrary;
  onMove?: () => void;
  selectAll?: {
    boardId: Board['id'];
    includeDescendants: boolean;
    totalSelectedItemsCount: number;
  };
  trackLocation?: AirActionTrackingLocation;
};

export const MoveItemsConfirmationModal = ({
  boards,
  clips,
  selectedBoard,
  selectedLibrary,
  onMove,
  selectAll,
  trackLocation = 'move-items-confirmation-modal',
  onClose,
}: AirModalProps<MoveItemsConfirmationModalProps>) => {
  const { showToast } = useToasts();
  const { goToBoardPage } = useGoToPrivateBoardPage();
  const addAssetsToBoardMutation = useAddAssetsToBoards();
  const { moveItems } = useMoveItems({ boards, clips, trackLocation: 'move-items-confirmation-modal' });
  const { createPrivateAssetBoardMembershipUpdateTask } = useCreatePrivateAssetBoardMembershipUpdateTask();
  const [pendingSelectAllOperation, setPendingSelectAllOperation] = useState<'add' | 'move'>();
  const isCreatingSelectAllTask = !!pendingSelectAllOperation;

  const createSelectAllTask = async (operation: 'add' | 'move') => {
    if (!selectAll || !selectedBoard) {
      return;
    }

    setPendingSelectAllOperation(operation);
    const result = await createPrivateAssetBoardMembershipUpdateTask({
      boardId: selectAll.boardId,
      includeDescendants: selectAll.includeDescendants,
      operation:
        operation === 'add'
          ? { type: 'add', targetBoards: [selectedBoard] }
          : { type: 'move', targetBoard: selectedBoard },
      totalSelectedItemsCount: selectAll.totalSelectedItemsCount,
      trackLocation,
    });

    if (result.error) {
      setPendingSelectAllOperation(undefined);
      showToast('An error occurred - please try again later');
      return;
    }

    onMove?.();
    onClose?.();
  };

  return (
    <Modal className="flex flex-col gap-6" dangerouslyBypassFocusLock isOpen>
      <div className="flex flex-col gap-2">
        <ModalTitle>Delete comments?</ModalTitle>
        <ModalDescription>
          {selectAll
            ? 'Moving these assets may delete comments from the current board. To keep these comments, consider adding the assets to another board instead.'
            : 'There are comments on this asset in the current board, and moving it will delete them. To keep these comments, consider adding the asset to another board instead.'}
        </ModalDescription>
      </div>
      <footer className="flex items-center justify-end gap-2">
        <Button appearance="ghost" color="gray" disabled={isCreatingSelectAllTask} onClick={onClose} size="large">
          Cancel
        </Button>

        <div className="flex gap-2">
          <Button
            appearance="outline"
            color="gray"
            disabled={!selectedBoard || isCreatingSelectAllTask}
            isLoading={selectAll ? pendingSelectAllOperation === 'add' : addAssetsToBoardMutation.isPending}
            onClick={() => {
              if (selectedBoard) {
                if (selectAll) {
                  void createSelectAllTask('add');
                  return;
                }

                addAssetsToBoardMutation.mutate(
                  {
                    clipIds: clips?.map((clip) => clip.id) || [],
                    boards: [selectedBoard],
                    trackLocation: 'move-items-confirmation-modal',
                  },
                  {
                    onSuccess: (_data, { clipIds, boards }) => {
                      showToast(
                        <>
                          {`${pluralize('item', clipIds.length, true)} added to `}
                          {boards.length === 1 ? (
                            <ToastLink
                              onClick={() =>
                                goToBoardPage({ board: boards[0], trackLocation: 'add-clips-to-board-toast' })
                              }
                            >
                              {boards[0].title}
                            </ToastLink>
                          ) : (
                            <>multiple boards</>
                          )}
                        </>,
                      );

                      onClose?.();
                    },
                  },
                );
              }
            }}
            size="large"
          >
            Add instead
          </Button>
          <Button
            appearance="filled"
            color="red"
            disabled={isCreatingSelectAllTask}
            isLoading={selectAll ? pendingSelectAllOperation === 'move' : moveItems.isPending}
            onClick={() => {
              if (selectAll) {
                void createSelectAllTask('move');
                return;
              }

              moveItems.mutate(
                {
                  selectedBoard,
                  selectedLibrary,
                },
                {
                  onSuccess: () => {
                    onClose?.();
                  },
                },
              );
            }}
            size="large"
          >
            Delete and move
          </Button>
        </div>
      </footer>
    </Modal>
  );
};
